import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { History, Save, Loader2, TableProperties, Plus, Trash2, Coins } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; } };

const HistoricalLeaveCard = ({ employeeId, onSaved }) => {
  const [records, setRecords] = useState([]);
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [extraPaid, setExtraPaid] = useState('');
  const [extraNote, setExtraNote] = useState('');
  const [savingExtra, setSavingExtra] = useState(false);
  const [form, setForm] = useState({ start_date: '', end_date: '', total_days: '', paid_days: '', lop_days: '' });
  const token = localStorage.getItem('token');
  const H = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const [h, b] = await Promise.all([
        axios.get(`${API}/api/leaves/historical/${employeeId}`, { headers: H }),
        axios.get(`${API}/api/leaves/carry-breakdown/${employeeId}`, { headers: H }),
      ]);
      setRecords(h.data.records || []);
      setExtraPaid(h.data.extra_paid_days ? String(h.data.extra_paid_days) : '');
      setExtraNote(h.data.extra_paid_note || '');
      setBreakdown(b.data);
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, [employeeId]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  // Auto-fill total from dates; keep paid defaulting to total, LOP = total - paid.
  const onForm = (field, value) => {
    setForm((f) => {
      const nf = { ...f, [field]: value };
      if ((field === 'start_date' || field === 'end_date') && nf.start_date && nf.end_date) {
        const s = new Date(nf.start_date), e = new Date(nf.end_date);
        if (e >= s) {
          const days = Math.round((e - s) / 86400000) + 1;
          if (!nf.total_days) nf.total_days = String(days);
        }
      }
      return nf;
    });
  };

  const addRecord = async () => {
    const total = parseFloat(form.total_days) || 0;
    if (!form.start_date || !form.end_date) { toast({ title: 'Start and end date are required', variant: 'destructive' }); return; }
    if (total <= 0) { toast({ title: 'Total days must be greater than 0', variant: 'destructive' }); return; }
    const paid = form.paid_days === '' ? total : (parseFloat(form.paid_days) || 0);
    const lop = form.lop_days === '' ? Math.max(0, total - paid) : (parseFloat(form.lop_days) || 0);
    setAdding(true);
    try {
      await axios.post(`${API}/api/leaves/historical/${employeeId}/record`, {
        start_date: form.start_date, end_date: form.end_date,
        total_days: total, paid_days: paid, lop_days: lop,
      }, { headers: H });
      toast({ title: 'Record added', description: 'Balance & carry-forward recalculated.' });
      setForm({ start_date: '', end_date: '', total_days: '', paid_days: '', lop_days: '' });
      await load();
      onSaved && onSaved();
    } catch (e) { toast({ title: 'Could not add', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
    finally { setAdding(false); }
  };

  const delRecord = async (id) => {
    try {
      await axios.delete(`${API}/api/leaves/historical/${employeeId}/record/${id}`, { headers: H });
      await load();
      onSaved && onSaved();
    } catch { toast({ title: 'Could not delete', variant: 'destructive' }); }
  };

  const saveExtra = async () => {
    setSavingExtra(true);
    try {
      await axios.put(`${API}/api/leaves/extra-paid/${employeeId}`, {
        extra_paid_days: parseFloat(extraPaid) || 0, note: extraNote || '',
      }, { headers: H });
      toast({ title: 'Extra paid days saved', description: 'Deducted from the live balance.' });
      await load();
      onSaved && onSaved();
    } catch { toast({ title: 'Could not save', variant: 'destructive' }); }
    finally { setSavingExtra(false); }
  };

  if (!employeeId) return null;
  const extraVal = parseFloat(extraPaid) || 0;

  return (
    <div className="mt-3 rounded-lg border border-indigo-200 bg-indigo-50/60 p-4" data-testid="historical-leave-card">
      <div className="flex items-center gap-2 mb-1">
        <History className="h-4 w-4 text-indigo-600" />
        <p className="text-sm font-semibold text-indigo-800">Historical Leave Records</p>
      </div>
      <p className="text-[11px] text-indigo-600 mb-3">Enter each past leave with its dates, total days, paid days and LOP (unpaid) days. Saving instantly recalculates carry-forward &amp; balance.</p>

      {loading ? (
        <div className="py-4 text-center text-gray-400"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
      ) : (
        <>
          {/* Existing records */}
          {records.length === 0 ? (
            <p className="text-xs text-gray-500 italic">No historical leave records yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs bg-white rounded-lg overflow-hidden" data-testid="hist-records-table">
                <thead className="bg-indigo-100 text-indigo-800">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Start</th>
                    <th className="px-2 py-1.5 text-left">End</th>
                    <th className="px-2 py-1.5 text-right">Total</th>
                    <th className="px-2 py-1.5 text-right">Paid</th>
                    <th className="px-2 py-1.5 text-right">LOP</th>
                    <th className="px-2 py-1.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((r) => (
                    <tr key={r.id} className="border-t border-gray-100" data-testid={`hist-record-${r.id}`}>
                      <td className="px-2 py-1.5">{fmt(r.start_date)}</td>
                      <td className="px-2 py-1.5">{fmt(r.end_date)}</td>
                      <td className="px-2 py-1.5 text-right font-medium">{r.total_days}</td>
                      <td className="px-2 py-1.5 text-right text-emerald-600">{r.paid_days}</td>
                      <td className="px-2 py-1.5 text-right text-rose-600">{r.lop_days || 0}</td>
                      <td className="px-2 py-1.5 text-right">
                        <button onClick={() => delRecord(r.id)} className="text-gray-400 hover:text-red-600" data-testid={`del-hist-${r.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Add record */}
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-2 bg-white rounded-lg p-2.5 border border-indigo-100">
            <div className="flex flex-col">
              <span className="text-[10px] text-gray-500 mb-0.5">Start date</span>
              <Input type="date" value={form.start_date} onChange={(e) => onForm('start_date', e.target.value)} className="h-8 text-xs" data-testid="hist-start-input" />
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] text-gray-500 mb-0.5">End date</span>
              <Input type="date" value={form.end_date} onChange={(e) => onForm('end_date', e.target.value)} className="h-8 text-xs" data-testid="hist-end-input" />
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] text-gray-500 mb-0.5">Total days</span>
              <Input type="number" min="0" step="0.5" value={form.total_days} onChange={(e) => onForm('total_days', e.target.value)} className="h-8 text-xs" data-testid="hist-total-input" />
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] text-gray-500 mb-0.5">Paid days</span>
              <Input type="number" min="0" step="0.5" value={form.paid_days} onChange={(e) => onForm('paid_days', e.target.value)} placeholder="= total" className="h-8 text-xs" data-testid="hist-paid-input" />
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] text-gray-500 mb-0.5">LOP days</span>
              <Input type="number" min="0" step="0.5" value={form.lop_days} onChange={(e) => onForm('lop_days', e.target.value)} placeholder="0" className="h-8 text-xs" data-testid="hist-lop-input" />
            </div>
            <div className="col-span-2 sm:col-span-5 flex justify-end">
              <Button size="sm" onClick={addRecord} disabled={adding} className="bg-indigo-600 hover:bg-indigo-700 h-8" data-testid="add-hist-record-btn">
                {adding ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />} Add Record
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Extra paid (advance) days */}
      <div className="mt-4 pt-3 border-t border-indigo-200" data-testid="extra-paid-box">
        <div className="flex items-center gap-1.5 mb-1">
          <Coins className="h-3.5 w-3.5 text-amber-600" />
          <p className="text-xs font-semibold text-amber-800">Extra Paid Days (Advance)</p>
        </div>
        <p className="text-[11px] text-amber-700 mb-2">
          Days already <b>paid in advance beyond entitlement</b>. Shown as <b>−{extraVal || 'X'}</b> and deducted from the live balance (carry-forward starts at 0). HR is reminded to adjust when this staff next applies for leave.
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col">
            <span className="text-[10px] text-gray-500 mb-0.5">Extra paid days</span>
            <Input type="number" min="0" step="0.5" value={extraPaid}
              onChange={(e) => setExtraPaid(e.target.value)}
              placeholder="e.g. 4" className="h-8 w-28 text-sm bg-white" data-testid="extra-paid-input" />
          </div>
          <div className="flex flex-col flex-1 min-w-[140px]">
            <span className="text-[10px] text-gray-500 mb-0.5">Note (optional)</span>
            <Input value={extraNote} onChange={(e) => setExtraNote(e.target.value)}
              placeholder="Reason" className="h-8 text-sm bg-white" data-testid="extra-paid-note-input" />
          </div>
          <Button size="sm" onClick={saveExtra} disabled={savingExtra}
            className="bg-amber-600 hover:bg-amber-700 h-8" data-testid="save-extra-paid-btn">
            {savingExtra ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Save
          </Button>
        </div>
      </div>

      {/* Carry-forward breakdown */}
      {breakdown && breakdown.rows?.length > 0 && (
        <div className="mt-4 pt-3 border-t border-indigo-200">
          <div className="flex items-center gap-1.5 mb-2">
            <TableProperties className="h-3.5 w-3.5 text-indigo-600" />
            <p className="text-xs font-semibold text-indigo-800">Carry-Forward Breakdown (max {breakdown.max_carry_forward}/yr)</p>
          </div>
          {!!breakdown.manual_adjustment && breakdown.manual_adjustment < 0 && (
            <p className="text-[11px] text-rose-600 mb-1">Includes Extra Paid (advance): <b>{breakdown.manual_adjustment}</b> day(s).</p>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-xs bg-white rounded-lg overflow-hidden">
              <thead className="bg-indigo-100 text-indigo-800">
                <tr>
                  <th className="px-2 py-1.5 text-left">Year</th>
                  <th className="px-2 py-1.5 text-right">Carried In</th>
                  <th className="px-2 py-1.5 text-right">Accrued</th>
                  <th className="px-2 py-1.5 text-right">Taken</th>
                  <th className="px-2 py-1.5 text-right">Lapsed</th>
                  <th className="px-2 py-1.5 text-right">Carried Out</th>
                </tr>
              </thead>
              <tbody>
                {breakdown.rows.map((r) => (
                  <tr key={r.year} className="border-t border-gray-100" data-testid={`carry-row-${r.year}`}>
                    <td className="px-2 py-1.5 font-medium">{r.year}</td>
                    <td className="px-2 py-1.5 text-right">{r.carried_in}</td>
                    <td className="px-2 py-1.5 text-right text-emerald-600">+{r.accrued}</td>
                    <td className="px-2 py-1.5 text-right text-red-600">-{r.taken}</td>
                    <td className="px-2 py-1.5 text-right text-amber-600">{r.lapsed > 0 ? `-${r.lapsed}` : '0'}</td>
                    <td className="px-2 py-1.5 text-right font-semibold">{r.carried_out}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoricalLeaveCard;
